/**
 * Canonical file types we group documents by in the UI.
 *
 * Used by the Pro Docs tab (and any future doc list) to:
 *  - pick the right icon next to a row
 *  - render a short filetype label ("PDF", "JPG", etc.) inline in the table
 *  - decide whether a file can be previewed inline vs offered as download
 *
 * Add new groups by extending this enum + the maps below in lockstep.
 */
export enum FileType {
  Pdf = 'pdf',
  Image = 'image',
  Word = 'word',
  Excel = 'excel',
  Text = 'text',
  Csv = 'csv',
  Html = 'html',
  Edi = 'edi',
  Video = 'video',
  Audio = 'audio',
  Other = 'other',
}

/** Map raw `fileinfo.filetype` extension (lowercase) to a FileType. */
const EXTENSION_TO_TYPE: Readonly<Record<string, FileType>> = {
  pdf: FileType.Pdf,
  jpg: FileType.Image,
  jpeg: FileType.Image,
  jpe: FileType.Image,
  png: FileType.Image,
  gif: FileType.Image,
  bmp: FileType.Image,
  webp: FileType.Image,
  tif: FileType.Image,
  tiff: FileType.Image,
  heic: FileType.Image,
  heif: FileType.Image,
  doc: FileType.Word,
  docx: FileType.Word,
  rtf: FileType.Word,
  xls: FileType.Excel,
  xlsx: FileType.Excel,
  txt: FileType.Text,
  csv: FileType.Csv,
  htm: FileType.Html,
  html: FileType.Html,
  edi: FileType.Edi,
  mov: FileType.Video,
  mp4: FileType.Video,
  mpeg: FileType.Video,
  wav: FileType.Audio,
};

/** Short label shown in the table (right after the file icon). */
export const FILE_TYPE_LABEL: Readonly<Record<FileType, string>> = {
  [FileType.Pdf]: 'PDF',
  [FileType.Image]: 'IMG',
  [FileType.Word]: 'DOC',
  [FileType.Excel]: 'XLS',
  [FileType.Text]: 'TXT',
  [FileType.Csv]: 'CSV',
  [FileType.Html]: 'HTML',
  [FileType.Edi]: 'EDI',
  [FileType.Video]: 'VID',
  [FileType.Audio]: 'WAV',
  [FileType.Other]: 'FILE',
};

/** Resolve a raw `fileinfo.filetype` string (e.g., 'PDF', 'jpeg') to a FileType. */
export function resolveFileType(filetype: string | null | undefined): FileType {
  const ext = (filetype || '').trim().toLowerCase();
  if (!ext) return FileType.Other;
  return EXTENSION_TO_TYPE[ext] ?? FileType.Other;
}

/** Resolve a full file name ("Screen Shot 2026.08.10.jpeg") by its final extension. */
export function resolveFileTypeFromName(fileName: string | null | undefined): FileType {
  const name = fileName || '';
  const dot = name.lastIndexOf('.');
  if (dot < 0) return FileType.Other;
  return resolveFileType(name.slice(dot + 1));
}

/** True for filetypes that can render inline in an `<embed>`/`<img>` lightbox. */
export function isPreviewable(t: FileType): boolean {
  return t === FileType.Pdf || t === FileType.Image;
}
